/****************************************************
 * Module Name:  brokerMasterComponent.js
 * Project:      Pansuriya Impex Sales & Manufacturing
 * Owner:        info@pansuriyaimpex.com
 *
 * Copyright (c) Pansuriya Impex
 * All rights reserved.
 ****************************************************/
import React, { Component } from "react";
import { DataGridPro, GridToolbar } from "@mui/x-data-grid-pro";
import { Box, Avatar, Chip, Tooltip, Button, Menu, MenuItem } from "@mui/material";
import BorderColorIcon from "@mui/icons-material/BorderColor";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import CircularProgress from "@mui/material/CircularProgress";
import renderCellExpand from "../common/dataGridCellExpand";
import PersonIcon from "@mui/icons-material/Person";
import PersonOffIcon from "@mui/icons-material/PersonOff";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import BrokerProxy from "../../utility/proxy/manager/brokerProxy";
import CustomerProxy from "../../utility/proxy/manager/customerProxy";
import EmployeeProxy from "../../utility/proxy/manager/employeeProxy";
import CommonSnackbar from "./commonSnackBar";
import EditBrokerModal from "../master/editBrokerModal";
import EditCustomerModal from "../master/editCustomerModal";
import EditEmployeeModal from "../master/editEmployeeModal";
import Constants from "../../utility/constants";
import Utility from "../../utility/utility";
import NoPermission from "../common/noPermission";
import PageTitle from "./pageTitle";

export class BrokerMasterComponent extends Component {
  constructor(props) {
    super(props);

    this.state = {
      brokers: [],
      showEditBrokerModal: false,

      filterMap: {},
      columnNames: [],

      editOperation: false,
      sourceData: {},
      employeeId: "",
      customerId: "",
      errorSnackbar: false,
      loadingButton: false,

      showBrokerLoadSpinner: true,

      showEditCustomerModal: false,
      showEditEmployeeModal: false,
      local_Permission: Constants.USER_PERMISSION.filter(
        (iPermission) =>
          iPermission.permissionid === Constants.PERMISSIONS.MASTER_BROKER
      ),

      anchorEl: null,
      openMenuId: null,
      openCustomersMenuId: null,
      anchorEl1: null,
      openMenuId1: null,
      openEmployeesMenuId: null,
    };
    this.fetchBrokerData(false);
  }

  componentDidMount() {
    PageTitle("MASTER_BROKER");
  }

  handleResponseData = (responseData) => {

    if (responseData) {
      const uniqueFilterValues = {
        createdby: Utility.getDistinctOptions(responseData, "createdby"),
        designation: Utility.getDistinctOptions(responseData, "designation"),
        grade: Utility.getDistinctOptions(responseData, "grade"),
        location: Utility.getDistinctOptions(responseData, "location"),
        locationcountry: Utility.getDistinctOptions(responseData, "locationcountry"),
        city: Utility.getDistinctOptions(responseData, "city"),
        state: Utility.getDistinctOptions(responseData, "state"),
        country: Utility.getDistinctOptions(responseData, "country"),
      };
      const filterMap = this.generateFilterMap(Array.isArray(responseData) ? responseData : [responseData]);
      this.setState({
        brokers: responseData,
        filterMap: filterMap,
        showBrokerLoadSpinner: false,
      });
      this.prepareColumns(filterMap, uniqueFilterValues);
    } else {
      this.setState({
        errorSnackbar: true,
        showBrokerLoadSpinner: false,
      });
    }
  };

  fetchBrokerData = (bAnimateSpinner) => {
    bAnimateSpinner && this.setState({ showBrokerLoadSpinner: true });
    if (
      (Constants.USER_PERMISSION.find(
        (iPermission) =>
          iPermission.permissionid === Constants.PERMISSIONS.MASTER_BROKER
      ) === undefined) && Constants.loggedInUser.usertype === 'E'
    ) {
      BrokerProxy.GetSingleEmployeesBrokerById(
        Constants.loggedInUser.id,
        this.handleResponseData
      );
    } else if (
      (Constants.USER_PERMISSION.find(
        (iPermission) =>
          iPermission.permissionid === Constants.PERMISSIONS.MASTER_BROKER
      ) === undefined) && Constants.loggedInUser.usertype === 'B'
    ) {
      BrokerProxy.GetSingleBrokerById(
        Constants.loggedInUser.id, (responseData) => {
          if (responseData) {
            const uniqueFilterValues = {
              createdby: Utility.getDistinctOptions(responseData, "createdby"),
              designation: Utility.getDistinctOptions(responseData, "designation"),
              grade: Utility.getDistinctOptions(responseData, "grade"),
              location: Utility.getDistinctOptions(responseData, "location"),
              locationcountry: Utility.getDistinctOptions(responseData, "locationcountry"),
              city: Utility.getDistinctOptions(responseData, "city"),
              state: Utility.getDistinctOptions(responseData, "state"),
              country: Utility.getDistinctOptions(responseData, "country"),
            };
            const filterMap = this.generateFilterMap(Array.isArray(responseData) ? responseData : [responseData]);
            this.setState({
              brokers: [responseData],
              filterMap: filterMap,
              showBrokerLoadSpinner: false,
            });
            this.prepareColumns(filterMap, uniqueFilterValues);
          } else {
            this.setState({
              errorSnackbar: true,
              showBrokerLoadSpinner: false,
            });
          }
        });
    } else {
      if (
        Constants.USER_PERMISSION.find(
          (iPermission) =>
            iPermission.permissionid === Constants.PERMISSIONS.MASTER_EMPLOYEE
        ) !== undefined
      ) {
        BrokerProxy.GetAllBrokerDetail(this.handleResponseData);
      } else {
        BrokerProxy.GetAllBrokerDetailByEmployeeId(
          Constants.loggedInUser.id,
          this.handleResponseData
        );
      }
    }
  };


  generateFilterMap = (responseData) => {
    const filterMap = {};

    // Process broker customers
    const brokerCustomers = responseData.flatMap((customer) =>
      customer.brokercustomerdtos.map((broker) => ({
        id: broker.customerid,
        username: broker.username,
      }))
    );
    filterMap.brokercustomerdtos = [
      ...new Set(
        brokerCustomers.map((broker) => `${broker.id} | ${broker.username}`)
      ),
    ];

    // Process employee employees
    const brokerEmployees = responseData.flatMap((customer) =>
      customer.employebrokerdtos.map((employee) => ({
        id: employee.employeid,
        username: employee.username,
      }))
    );

    filterMap.employebrokerdtos = [
      ...new Set(
        brokerEmployees.map(
          (employee) => `${employee.id} | ${employee.username}`
        )
      ),
    ];

    return filterMap;
  };

  prepareColumns = (filterMap, uniqueFilterValues) => {
    let columnNames = [];
    const columnData = this.initialColumns(uniqueFilterValues);

    columnData.forEach(iCol => {
      if (iCol.field === "brokercustomerdtos") {
        iCol.type = "singleSelect";
        iCol.valueOptions = filterMap ? filterMap.brokercustomer : [];
        iCol.filterOperators = Utility.customFilters(filterMap, iCol.field, 'customerid', 'username');
      }
      if (iCol.field === "employebrokerdtos") {
        iCol.type = "singleSelect";
        iCol.valueOptions = filterMap ? filterMap.brokeremployee : [];
        iCol.filterOperators = Utility.customFilters(filterMap, iCol.field, 'employeid', 'username');
      }
      columnNames.push(iCol);
    });
    this.setState({
      columnNames: columnNames
    })
  }

  initialColumns = (combinedFilterValues) => [
    {
      field: "id",
      headerName: "ID",
      headerAlign: "center",
      align: "center",
      minWidth: 50,
    },
    {
      field: "companyname",
      headerName: "COMPANY NAME",
      headerAlign: "center",
      align: "center",
      minWidth: 150,
      flex: 1,
      type: "singleSelect",
      valueOptions: combinedFilterValues ? combinedFilterValues.companyname : [],
      filterOperators: Utility.combinedFilters(),
      renderCell: renderCellExpand,
    },
    {
      field: "title",
      headerName: "TITLE",
      headerAlign: "center",
      align: "center",
      width: 60,
      flex: 1,
      type: "singleSelect",
      valueOptions: combinedFilterValues ? combinedFilterValues.title : [],
      filterOperators: Utility.combinedFilters(),
      renderCell: renderCellExpand,
    },
    {
      field: "first_name",
      headerName: "FIRST NAME",
      headerAlign: "center",
      align: "center",
      minWidth: 100,
      flex: 1,
      renderCell: renderCellExpand,
    },
    {
      field: "last_name",
      headerName: "LAST NAME",
      headerAlign: "center",
      align: "center",
      minWidth: 100,
      flex: 1,
      renderCell: renderCellExpand,
    },
    {
      field: "phonecode",
      headerName: "PHONE CODE",
      headerAlign: "center",
      align: "center",
      width: 100,
      type: "singleSelect",
      valueOptions: combinedFilterValues ? combinedFilterValues.phonecode : [],
      filterOperators: Utility.combinedFilters(),
      renderCell: renderCellExpand,
    },
    {
      field: "phone",
      headerName: "PHONE",
      headerAlign: "center",
      align: "center",
      minWidth: 130,
      flex: 1,
      renderCell: renderCellExpand,
    },
    {
      field: "email",
      headerName: "EMAIL",
      headerAlign: "center",
      align: "center",
      minWidth: 220,
      flex: 1,
      renderCell: renderCellExpand,
    },
    {
      field: "username",
      headerName: "USER",
      headerAlign: "center",
      align: "center",
      minWidth: 100,
      flex: 1,
      renderCell: renderCellExpand,
    },
    {
      field: "country",
      headerName: "COUNTRY",
      headerAlign: "center",
      align: "center",
      width: 100,
      type: "singleSelect",
      valueOptions: combinedFilterValues ? combinedFilterValues.country : [],
      filterOperators: Utility.combinedFilters(),
      renderCell: renderCellExpand,
    },
    {
      field: "address",
      headerName: "ADDRESS",
      headerAlign: "center",
      align: "center",
      minWidth: 130,
      flex: 1,
      renderCell: renderCellExpand,
    },
    {
      field: "middle_name",
      headerName: "MIDDLE NAME",
      headerAlign: "center",
      align: "center",
      minWidth: 100,
      flex: 1,
      renderCell: renderCellExpand,
    },
    {
      field: "fax",
      headerName: "FAX",
      headerAlign: "center",
      align: "center",
      minWidth: 130,
      flex: 1,
      renderCell: renderCellExpand,
    },
    {
      field: "address2",
      headerName: "ADDRESS2",
      headerAlign: "center",
      align: "center",
      minWidth: 130,
      flex: 1,
      renderCell: renderCellExpand,
    },
    {
      field: "city",
      headerName: "CITY",
      headerAlign: "center",
      align: "center",
      width: 100,
      type: "singleSelect",
      valueOptions: combinedFilterValues ? combinedFilterValues.city : [],
      filterOperators: Utility.combinedFilters(),
      renderCell: renderCellExpand,
    },
    {
      field: "state",
      headerName: "STATE",
      headerAlign: "center",
      align: "center",
      width: 100,
      type: "singleSelect",
      valueOptions: combinedFilterValues ? combinedFilterValues.state : [],
      filterOperators: Utility.combinedFilters(),
      renderCell: renderCellExpand,
    },
    {
      field: "pincode",
      headerName: "PIN CODE",
      headerAlign: "center",
      align: "center",
      minWidth: 130,
      flex: 1,
      type: "singleSelect",
      valueOptions: combinedFilterValues ? combinedFilterValues.pincode : [],
      filterOperators: Utility.combinedFilters(),
      renderCell: renderCellExpand,
    },
    {
      field: "emergencycontactname",
      headerName: "EMERGENCY CONTACT NAME",
      headerAlign: "center",
      align: "center",
      minWidth: 130,
      flex: 1,
      renderCell: renderCellExpand,
    },
    {
      field: "emergencyphone",
      headerName: "EMERGENCY PHONE",
      headerAlign: "center",
      align: "center",
      minWidth: 130,
      flex: 1,
      renderCell: renderCellExpand,
    },
    {
      field: "emergencyaddress",
      headerName: "EMERGENCY ADDRESS",
      headerAlign: "center",
      align: "center",
      minWidth: 130,
      flex: 1,
      renderCell: renderCellExpand,
    },
    {
      field: "gstinno",
      headerName: "GSTN",
      headerAlign: "center",
      align: "center",
      minWidth: 100,
      flex: 1,
    },
    {
      field: "panno",
      headerName: "PAN",
      headerAlign: "center",
      align: "center",
      minWidth: 100,
      flex: 1,
    },
    {
      field: "created",
      headerName: "CREATED AT",
      headerAlign: "center",
      align: "center",
      minWidth: 180,
      flex: 1,
      renderCell: (params) => Utility.ConvertUtcToLocalTime(params.value),
    },
    {
      field: "createdby",
      headerName: "CREATED BY",
      width: 170,
      renderCell: renderCellExpand,
      type: "singleSelect",
      valueOptions: combinedFilterValues ? combinedFilterValues.createdby : [],
      filterOperators: Utility.combinedFilters(),
      headerAlign: "center",
      align: "center",
    },
    {
      field: "locationid",
      headerName: "LOCATION ID",
      headerAlign: "center",
      align: "center",
      minWidth: 50,
      flex: 0.5,
      renderCell: renderCellExpand,
    },
    {
      field: "location",
      headerName: "LOCATION",
      width: 100,
      headerAlign: "center",
      align: "center",
      type: "singleSelect",
      valueOptions: combinedFilterValues ? combinedFilterValues.location : [],
      filterOperators: Utility.combinedFilters(),
      renderCell: renderCellExpand,
    },
    {
      field: "locationcountry",
      headerName: "LOCATION COUNTRY",
      width: 150,
      headerAlign: "center",
      align: "center",
      type: "singleSelect",
      valueOptions: combinedFilterValues ? combinedFilterValues.locationcountry : [],
      filterOperators: Utility.combinedFilters(),
      renderCell: renderCellExpand,
    },
    {
      field: "commission",
      headerName: "COMMISSION",
      headerAlign: "center",
      align: "center",
      minWidth: 100,
      flex: 1,
    },

    {
      field: "designation",
      headerName: "DESIGNATION",
      headerAlign: "center",
      align: "center",
      minWidth: 130,
      flex: 1,
      type: "singleSelect",
      valueOptions: combinedFilterValues ? combinedFilterValues.designation : [],
      filterOperators: Utility.combinedFilters(),
      renderCell: renderCellExpand,
    },
    {
      field: "usertype",
      headerAlign: "center",
      align: "center",
      headerName: "TYPE",
      minWidth: 100,
      flex: 1,
      valueGetter: (params) => Constants.USER_TYPE[params.value],
      renderCell: (params) => Constants.USER_TYPE[params.value],
    },
    {
      field: "grade",
      headerName: "GRADE",
      headerAlign: "center",
      align: "center",
      minWidth: 60,
      type: "singleSelect",
      valueOptions: combinedFilterValues ? combinedFilterValues.grade : [],
      filterOperators: Utility.combinedFilters(),
      flex: 0.5,
    },
    {
      field: "employeeid",
      headerName: "EMPLOYEE ID",
      headerAlign: "center",
      align: "center",
      minWidth: 100,
      flex: 0.5,
      renderCell: renderCellExpand,
    },
    {
      field: "brokercustomerdtos",
      headerName: "ASSIGNED TO CUSTOMER",
      headerAlign: "center",
      align: "center",
      width: 400,
      renderCell: (params) => params.value.length === 0 ? null : (
        <Box>
          <Button
            aria-controls={this.state.openCustomersMenuId === params.id ? 'role-customers-menu' : undefined}
            aria-haspopup="true"
            onClick={(event) => this.handleCustomerMenuClick(event, params.id)}
          >
            View Customers
          </Button>
          <Menu
            id="role-customers-menu"
            anchorEl={this.state.anchorEl}
            open={this.state.openCustomersMenuId === params.id}
            onClose={this.handleCustomerMenuClose}
            sx={{
              height: "40rem"
            }}
          >
            {params.value.map((item, index) => (
              <MenuItem>
                <Chip
                  key={index}
                  clickable
                  avatar={<Avatar sx={{ width: 'auto', fontSize: 10 }}>{item.customerid}</Avatar>}
                  label={item.username}
                  onClick={() => {
                    this.setState({
                      showEditCustomerModal: true,
                      editOperation: false,
                      sourceData: undefined,
                      customerId: item.customerid,
                      loadingButton: false,
                      anchorEl: null,
                      openCustomersMenuId: null,
                    });
                  }}
                  disabled={
                    Constants.USER_PERMISSION.find(
                      (iPermission) =>
                        iPermission.permissionid ===
                        Constants.PERMISSIONS.MASTER_CUSTOMER &&
                        iPermission.type === "UPDATE"
                    ) === undefined
                  }
                  sx={{ margin: "0px 10px 10px 0px", fontSize: 10 }}
                />
              </MenuItem>
            ))}
          </Menu>
        </Box>
      ),
      valueFormatter: (params) => {
        if (!params.value || params.value.length === 0) return "";
        return params.value
          .map((item) => `${item.customerid} | ${item.username}`)
          .join(",\n");
      },
      sortComparator: (v1, v2) => Utility.sortOperatorFn(v1, v2, "username")
    },
    {
      field: "employebrokerdtos",
      headerName: "ASSIGNED TO EMPLOYEE",
      headerAlign: "center",
      align: "center",
      width: 400,
      renderCell: (params) => params.value.length === 0 ? null : (
        <Box>
          <Button
            aria-controls={this.state.openEmployeesMenuId === params.id ? 'role-employee-menu' : undefined}
            aria-haspopup="true"
            onClick={(event) => this.handleEmployeeMenuClick(event, params.id)}
          >
            View Employees
          </Button>
          <Menu
            id="role-employee-menu"
            anchorEl={this.state.anchorEl1}
            open={this.state.openEmployeesMenuId === params.id}
            onClose={this.handleEmployeeMenuClose}
            sx={{
              height: "40rem"
            }}
          >
            {params.value.map((item, index) => (
              <MenuItem>
                <Chip
                  key={index}
                  clickable
                  avatar={<Avatar sx={{ width: 'auto', fontSize: 10 }}>{item.employeid}</Avatar>}
                  label={item.username}
                  onClick={() => {
                    this.setState({
                      showEditEmployeeModal: true,
                      editOperation: false,
                      sourceData: undefined,
                      employeeId: item.employeid,
                      loadingButton: false,
                      anchorEl1: null,
                      openEmployeesMenuId: null,
                    });
                  }}
                  disabled={
                    Constants.USER_PERMISSION.find(
                      (iPermission) =>
                        iPermission.permissionid ===
                        Constants.PERMISSIONS.MASTER_EMPLOYEE &&
                        iPermission.type === "UPDATE"
                    ) === undefined
                  }
                  sx={{ margin: "0px 10px 10px 0px", fontSize: 10 }}
                />
              </MenuItem>
            ))}
          </Menu>
        </Box>
      ),
      valueFormatter: (params) => {
        if (!params.value || params.value.length === 0) return "";
        return params.value
          .map((item) => `${item.employeid} | ${item.username}`)
          .join(",\n");
      },
      sortComparator: (v1, v2) => Utility.sortOperatorFn(v1, v2, "username")
    },
    {
      field: "deleted",
      headerName: "DELETED",
      headerAlign: "center",
      align: "center",
      minWidth: 90,
      flex: 1,
      renderCell: (params) => {
        return params.value === false ? (
          <Tooltip title="Active">
            <Box>
              <PersonIcon color="success" />
            </Box>
          </Tooltip>
        ) : (
          <Tooltip title="Inactive">
            <Box>
              <PersonOffIcon color="error" />
            </Box>
          </Tooltip>
        );
      },
    },
    {
      field: "disabled",
      headerName: "DISABLED",
      headerAlign: "center",
      align: "center",
      minWidth: 90,
      flex: 1,
      renderCell: (params) => {
        return params.value === false ? (
          <Tooltip title="Enable">
            <Box>
              <PersonIcon color="success" />
            </Box>
          </Tooltip>
        ) : (
          <Tooltip title="Disable">
            <Box>
              <PersonOutlineOutlinedIcon color="error" />
            </Box>
          </Tooltip>
        );
      },
    },
    {
      field: "edit",
      headerName: "EDIT",
      headerAlign: "center",
      align: "center",
      minWidth: 50,
      flex: 0.5,
      sortable: false,
      export: false,
      filterable: false,
      renderCell: (params) => {
        return (
          <Box
            style={{ textAlign: "center", cursor: this.state.local_Permission.length > 1 ? "pointer" : "not-allowed" }}
            onClick={this.state.local_Permission.length > 1 ? () => {
              this.setState({
                showEditBrokerModal: true,
                editOperation: true,
                sourceData: params.row,
                loadingButton: false,
              });
            } : null}
          >
            <BorderColorIcon color={this.state.local_Permission.length > 1 ? "primary" : "disabled"} />
          </Box>
        );
      },
    },
  ];

  handleCustomerMenuClick = (event, id) => {
    this.setState({
      anchorEl: event.currentTarget,
      openCustomersMenuId: id,
    });
  };

  handleCustomerMenuClose = () => {
    this.setState({
      anchorEl: null,
      openCustomersMenuId: null,
    });
  };

  handleEmployeeMenuClick = (event, id) => {
    this.setState({
      anchorEl1: event.currentTarget,
      openEmployeesMenuId: id,
    });
  };

  handleEmployeeMenuClose = () => {
    this.setState({
      anchorEl1: null,
      openEmployeesMenuId: null,
    });
  };

  hideEditBrokerModal = () => {
    this.setState({
      showEditBrokerModal: false,
    });
  };

  hideAddUserModal = () => {
    this.setState({
      showEditCustomerModal: false,
      showEditEmployeeModal: false,
    });
  };

  handleValidation = (brokerData) => {
    //TODO do validation if needed here
    return true;
  };

  editBroker = (brokerId, brokerData) => {
    let brokerid = brokerId;
    this.setState({ loadingButton: true });
    //Update existing Broker, and once user is updated on call refresh the list of current brokers
    BrokerProxy.UpdateBroker(brokerid, brokerData, (response) => {
      if (response) {
        this.hideEditBrokerModal();
        this.fetchBrokerData(true);
        this.setState({ loadingButton: false });
      } else {
        this.setState({ errorSnackbar: true });
        this.setState({ loadingButton: false });
      }
    });
  };

  updateCustomer = (customerId, userData) => {
    this.setState({ loadingButton: true });
    //Update existing User, and once user is updated on call refresh the list of current users
    CustomerProxy.UpdateCustomer(customerId, userData, (response) => {
      if (response) {
        this.hideAddUserModal();
        this.setState({ loadingButton: false });
      } else {
        this.setState({ errorSnackbar: true });
        this.setState({ loadingButton: false });
      }
    });
  };

  updateEmployee = (employeeId, userData) => {
    this.setState({ loadingButton: true });
    //Update existing User, and once user is updated on call refresh the list of current users
    EmployeeProxy.UpdateEmployee(employeeId, userData, (response) => {
      if (response) {
        this.hideAddUserModal();
        this.setState({ loadingButton: false });
      } else {
        this.setState({ errorSnackbar: true });
        this.setState({ loadingButton: false });
      }
    });
  };

  render() {
    let allowed = Utility.isReadAllowed(this.state.local_Permission) || (Constants.USER_PERMISSION.find(
      (iPermission) =>
        iPermission.permissionid ===
        Constants.PERMISSIONS.MASTER_BROKER
    ) === undefined);
    if (!allowed && Constants.loggedInUser.usertype !== "B") {
      return <NoPermission />;
    }
    const rows = this.state.brokers;
    const columns =
      (Utility.isUpdateAllowed(this.state.local_Permission) &&
        this.props.editButton) || (Constants.USER_PERMISSION.find(
          (iPermission) =>
            iPermission.permissionid ===
            Constants.PERMISSIONS.MASTER_BROKER
        ) === undefined)
        ? this.state.columnNames
        : this.state.columnNames.filter((col) => col.field !== "edit");
    return (
      <Box>
        <CommonSnackbar
          show={this.state.errorSnackbar}
          message={<Box>{Constants.APiFailErrorMessage}</Box>}
          variant={"error"}
          autoHide={Constants.SnackBarCloseTimer}
          handleClose={() => this.setState({ errorSnackbar: false })}
          title="Failed"
        />
        {this.state.showEditBrokerModal ? (
          <EditBrokerModal
            Show={true}
            handleClose={this.hideEditBrokerModal}
            handleSave={this.editBroker}
            handleValidation={this.handleValidation}
            editOperation={this.state.editOperation}
            sourceData={this.state.sourceData}
            loading={this.state.loadingButton}
            saveButton={true}
          />
        ) : (
          <Box />
        )}
        {this.state.showEditCustomerModal ? (
          <EditCustomerModal
            Show={true}
            handleClose={this.hideAddUserModal}
            handleSave={this.updateCustomer}
            handleValidation={this.handleValidation}
            editOperation={this.state.editOperation}
            sourceData={this.state.sourceData}
            userId={this.state.customerId}
            loading={this.state.loadingButton}
            saveButton={this.props.editButton}
          />
        ) : (
          <Box />
        )}
        {this.state.showEditEmployeeModal ? (
          <EditEmployeeModal
            Show={true}
            handleClose={this.hideAddUserModal}
            handleSave={this.updateEmployee}
            handleValidation={this.handleValidation}
            editOperation={this.state.editOperation}
            sourceData={this.state.sourceData}
            userId={this.state.employeeId}
            loading={this.state.loadingButton}
            saveButton={this.props.editButton}
          />
        ) : (
          <Box />
        )}

        <Box px={1}>
          <Box
            sx={{
              backgroundColor: "#fff",
              color: "#000",
              width: "100%",
              position: "relative",
              "& .MuiTablePagination-selectLabel": {
                marginTop: "15px",
              },
              "& .MuiTablePagination-displayedRows": {
                marginTop: "15px",
              },
              "& .css-1jbbcbn-MuiDataGrid-columnHeaderTitle": {
                fontWeight: Constants.defaultFontWeight,
              },
              "& .css-cc8tf1": {
                fontWeight: Constants.defaultFontWeight,
              },
              height: this.props.editButton ? "85vh" : "60vh",
            }}
          >
            <DataGridPro
              disableRowSelectionOnClick={true}
              slots={{ toolbar: GridToolbar }}
              rows={rows}
              columns={columns}
              density="compact"
              getRowId={(row) => row.id}
              getRowHeight={() => "auto"}
              loading={this.state.showBrokerLoadSpinner}
              sx={{
                "& .MuiDataGrid-columnHeader": {
                  color: "#fff",
                  backgroundColor: "grey",
                },
                "&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell": {
                  py: "4px",
                },
                "&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell": {
                  py: "12px",
                },
                "&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell": {
                  py: "18px",
                },
                fontSize: Constants.defaultTableFontSize,
                fontWeight: Constants.defaultFontWeight,
              }}
              initialState={{
                columns: {
                  columnVisibilityModel: {
                    // Hide columns status and traderName, the other columns will remain visible
                    id: false,
                    locationid: false,
                    usertype: false,
                    location: false,
                    created: false,
                    createdby: false,
                    locationcountry: false,
                  },
                },
                pinnedColumns: { right: ['edit'] },
                pagination: {
                  paginationModel: Constants.pageSize,
                },
              }}
              pageSizeOptions={Constants.pageSizeOptions}
              pagination
              slotProps={{
                toolbar: {
                  showQuickFilter: true,
                  quickFilterProps: { debounceMs: 500 },
                },
              }}
            />
            <Box style={{ position: "absolute", top: 5, right: "270px" }}>
              {this.state.showBrokerLoadSpinner ? (
                <CircularProgress size="1rem" />
              ) : (
                <Tooltip title="Reload List">
                  <RefreshOutlinedIcon
                    color="primary"
                    fontSize="medium"
                    onClick={() => this.fetchBrokerData(true)}
                    sx={{ cursor: "pointer" }}
                  />
                </Tooltip>
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    );
  }
}

export default BrokerMasterComponent;
